const toNumberOrNull = (value) => {
	if (value === null || value === undefined || value === "") {
		return null;
	}
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

const ensureArray = (value) => {
	if (Array.isArray(value)) {
		return value;
	}
	if (value && Array.isArray(value.result)) {
		return value.result;
	}
	if (value && Array.isArray(value.data)) {
		return value.data;
	}
	return [];
};

export const extractEnvelope = (payload) => {
	if (!payload) {
		return { data: [], meta: {} };
	}

	return {
		data: ensureArray(payload),
		meta: payload.meta || {},
	};
};

export const parseDecimal = (value) => {
	if (value === null || value === undefined || value === "") {
		return null;
	}

	const normalized =
		typeof value === "string" ? value.replace(/,/g, "").trim() : value;
	const parsed = Number(normalized);

	return Number.isFinite(parsed) ? parsed : null;
};

export const deriveSubscriptionStatus = ({
	isActive,
	archivedAt,
	deletedAt,
} = {}) => {
	if (deletedAt) {
		return "deleted";
	}
	if (archivedAt) {
		return "archived";
	}
	if (isActive === false) {
		return "inactive";
	}
	return "active";
};

export const normalizeMetadataItem = (item = {}) => {
	const id = item.id ?? item.value ?? null;
	const name = item.name ?? item.label ?? item.title ?? "";
	return {
		id: toNumberOrNull(id) ?? id,
		name,
		note: item.note || "",
		raw: item,
	};
};

export const normalizeMetadata = (payload) => {
	const { data } = extractEnvelope(payload);
	return data.map((item) => normalizeMetadataItem(item));
};

export const normalizeSubscription = (item = {}) => {
	const amountMonthly =
		item.amountMonthly ?? item.monthlyAmount ?? item.amount ?? null;
	const amountTotal = item.amountTotal ?? item.totalAmount ?? null;
	const company = item.company || {};
	const revenueShares = Array.isArray(item.revenueShares)
		? item.revenueShares
		: [];

	const companyId =
		toNumberOrNull(item.companyId) ??
		toNumberOrNull(company.id) ??
		toNumberOrNull(company.companyId);
	const companyName =
		item.companyName ?? company.name ?? company.companyName ?? "";

	return {
		id: toNumberOrNull(item.id) ?? item.id ?? null,
		companyId,
		companyName,
		serviceId: toNumberOrNull(item.serviceId) ?? item.serviceId ?? null,
		financialImpactId:
			toNumberOrNull(item.financialImpactId) ?? item.financialImpactId ?? null,
		durationId:
			toNumberOrNull(item.durationId) ??
			toNumberOrNull(item.subscriptionDurationId) ??
			item.durationId ??
			item.subscriptionDurationId ??
			null,
		invoicingPeriodId:
			toNumberOrNull(item.invoicingPeriodId) ?? item.invoicingPeriodId ?? null,
		amountMonthly:
			amountMonthly !== null && amountMonthly !== undefined
				? String(amountMonthly)
				: null,
		amountMonthlyNumber: parseDecimal(amountMonthly),
		amountTotal:
			amountTotal !== null && amountTotal !== undefined
				? String(amountTotal)
				: null,
		amountTotalNumber: parseDecimal(amountTotal),
		isActive: typeof item.isActive === "boolean" ? item.isActive : null,
		startDate: item.startDate ?? null,
		endDate: item.endDate ?? null,
		createdAt: item.createdAt ?? null,
		archivedAt: item.archivedAt ?? null,
		deletedAt: item.deletedAt ?? null,
		updatedAt: item.updatedAt ?? null,
		notes: item.notes ?? "",
		status: deriveSubscriptionStatus(item),
		revenueShares: revenueShares.map((share = {}) => {
			const inputFormat = share.inputFormat || {};
			const normalizedRevenueShare = share.revenueShare;
			const parsedShare = Number(normalizedRevenueShare);
			return {
				id: toNumberOrNull(share.id) ?? share.id ?? null,
				inputFormatId:
					toNumberOrNull(share.inputFormatId) ??
					toNumberOrNull(inputFormat.id) ??
					share.inputFormatId ??
					inputFormat.id ??
					null,
				inputFormatType:
					inputFormat.type ?? share.inputFormatType ?? inputFormat.name ?? "",
				inputFormatName:
					inputFormat.name ?? share.inputFormatName ?? inputFormat.type ?? "",
				sortOrder:
					typeof share.sortOrder === "number"
						? share.sortOrder
						: typeof inputFormat.sortOrder === "number"
							? inputFormat.sortOrder
							: null,
				revenueShare: Number.isFinite(parsedShare)
					? parsedShare
					: toNumberOrNull(normalizedRevenueShare),
				raw: share,
			};
		}),
		raw: item,
	};
};

export const normalizeSubscriptions = (payload) => {
	const { data, meta } = extractEnvelope(payload);
	return {
		list: data.map((item) => normalizeSubscription(item)),
		meta,
	};
};

export const isAbortError = (error) => {
	if (!error) {
		return false;
	}
	const name = error.name || error.code;
	return (
		name === "CanceledError" || name === "AbortError" || name === "ERR_CANCELED"
	);
};

export const mapById = (items = []) => {
	return items.reduce((acc, item) => {
		if (item && item.id !== undefined && item.id !== null) {
			acc[item.id] = item;
		}
		return acc;
	}, {});
};

/**
 * Converts an Immutable collection or plain array to a plain array
 * @param {*} collection - Immutable List/Map or plain array
 * @returns {Array} Plain JavaScript array
 */
export const toPlainArray = (collection) => {
	if (!collection) {
		return [];
	}
	if (collection.toJS) {
		return collection.toJS();
	}
	if (collection.valueSeq) {
		return collection.valueSeq().toArray();
	}
	if (collection.toArray) {
		return collection.toArray();
	}
	if (Array.isArray(collection)) {
		return collection;
	}
	return [];
};

/**
 * Formats a numeric value as currency (EUR)
 * @param {string|number} value - Value to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value) => {
	if (value === null || value === undefined || value === "") {
		return "-";
	}
	const numberValue = Number(value);
	if (!Number.isFinite(numberValue)) {
		return "-";
	}
	return new Intl.NumberFormat(undefined, {
		style: "currency",
		currency: "EUR",
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(numberValue);
};

/**
 * Checks if a collection has items
 * Works with both Immutable and plain JS collections
 * @param {*} collection - Collection to check
 * @returns {boolean} True if collection is empty
 */
export const isEmptyCollection = (collection) => {
	if (!collection) {
		return true;
	}
	// Immutable collection
	if (typeof collection.size === "number") {
		return collection.size === 0;
	}
	// Plain array
	if (Array.isArray(collection)) {
		return collection.length === 0;
	}
	// Plain object
	if (typeof collection === "object") {
		return Object.keys(collection).length === 0;
	}
	return true;
};

export default {
	extractEnvelope,
	normalizeSubscriptions,
	normalizeSubscription,
	normalizeMetadata,
	normalizeMetadataItem,
	deriveSubscriptionStatus,
	parseDecimal,
	isAbortError,
	mapById,
	toNumberOrNull,
	toPlainArray,
	formatCurrency,
	isEmptyCollection,
};
